import 'hammerjs';

import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import {
    ConfigCatProvider,
    createConsoleLogger,
    LogLevel,
    PollingMode,
} from 'configcat-react';
import { AccessProvider } from 'contexts/AccessContext';
import { ConfiguredAppProvider } from 'contexts/ConfiguredAppProvider';
import { ConfigCatUserProvider } from 'contexts/configcat/ConfigCatUserContext';
import { FeatureFlagProvider } from 'contexts/FeatureFlagContext';
import { NotificationProvider } from 'contexts/NotificationContext';
import { PendoProvider } from 'contexts/PendoContext';
import { PermissionsProvider } from 'contexts/PermissionsContext';
import { RouteEntityProvider } from 'contexts/RouteEntityContext';
import { KCFStylesProvider } from 'contexts/ThemeContext';
import { UserProvider } from 'contexts/UserContext';
import { UserSettingsProvider } from 'contexts/user_settings/UserSettingsStore';
import { IssueEventsProvider } from 'pages/Desk/issues/issue_details/IssueEventsProvider';
import { GlobalErrorPage } from 'pages/ErrorPage';
import { Login } from 'pages/Login/Login';
import { MainPage } from 'pages/MainPage';
import { StrictMode, useCallback, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Environment } from 'utils/EnvironmentHelper';
import {
    useAuthState,
    usePostLoginRedirect,
    useWaitForAuthentication,
} from 'utils/login';
import { qaUserSwapAutoSignin, qaUserSwapAutoSigninCreds } from 'utils/qaSessionStash';
import { queryClient } from 'utils/query';
import { Spinner } from 'components/spinner/Spinner';

const ConfigCatPollingSeconds = 95;

const Splash = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

function LoadingSplash({ text }: { text?: string }) {
    return (
        <Splash>
            <div className="flex flex-column align-center gap-5">
                <Spinner />
                {text && <div>{text}</div>}
            </div>
        </Splash>
    );
}

/**
 * Handles bouncing between the main app and the login screen based on the
 * client's auth state.
 */
function AppWithAuth(): JSX.Element {
    const [authState, setAuthState] = useAuthState();
    const onSignIn = useCallback(() => setAuthState('signedin'), [setAuthState]);
    useWaitForAuthentication(onSignIn, authState === 'loading' ? 100 : null);
    usePostLoginRedirect(authState === 'signedin');

    useEffect(() => {
        qaUserSwapAutoSignin();
    }, []);

    const isSwapping = qaUserSwapAutoSigninCreds.useWatch();

    if (isSwapping) {
        return <LoadingSplash text="Swapping QA User..." />;
    }

    switch (authState) {
        case 'signedin':
            return <App />;
        case 'signedout':
            return <Login />;
        default:
            return <LoadingSplash />;
    }
}

function App() {
    const logger = createConsoleLogger(
        process.env.NODE_ENV === 'production' ? LogLevel.Off : LogLevel.Info,
    );

    return (
        <StrictMode>
            <QueryClientProvider client={queryClient}>
                <Sentry.ErrorBoundary fallback={GlobalErrorPage}>
                    <KCFStylesProvider>
                        <ConfiguredAppProvider className="full-height">
                            <UserProvider>
                                <UserSettingsProvider>
                                    <BrowserRouter>
                                        <RouteEntityProvider>
                                            <ConfigCatProvider
                                                sdkKey={Environment.configCatApi}
                                                pollingMode={PollingMode.AutoPoll}
                                                options={{
                                                    logger,
                                                    pollIntervalSeconds:
                                                        ConfigCatPollingSeconds,
                                                }}
                                            >
                                                <ConfigCatUserProvider>
                                                    <PendoProvider>
                                                        <AccessProvider>
                                                            <PermissionsProvider>
                                                                <FeatureFlagProvider>
                                                                    <NotificationProvider>
                                                                        <IssueEventsProvider>
                                                                            <MainPage />
                                                                        </IssueEventsProvider>
                                                                    </NotificationProvider>
                                                                </FeatureFlagProvider>
                                                            </PermissionsProvider>
                                                        </AccessProvider>
                                                    </PendoProvider>
                                                </ConfigCatUserProvider>
                                            </ConfigCatProvider>
                                        </RouteEntityProvider>
                                    </BrowserRouter>
                                </UserSettingsProvider>
                            </UserProvider>
                        </ConfiguredAppProvider>
                    </KCFStylesProvider>
                </Sentry.ErrorBoundary>
            </QueryClientProvider>
        </StrictMode>
    );
}

export { AppWithAuth as App };
